import type {
  AppSettings,
  Book,
  BibleBook as BibleTrackedBook,
  BookStatusHistory,
  CompletedReadingSession,
  EpubProgressEntry,
  Instant,
  LocalDate,
  ReadingProgress,
  ReadingProgressEntry,
  ReadingSession,
  ReadingTimeContribution
} from '../domain/entities/types';
import type { BibleBookCode, BibleChapterId } from '../domain/bibleCanon';
import { BIBLE_TOTAL_CHAPTERS, bibleChapterRange, nextBibleChapter, normalizeBibleChapterIds, parseBibleChapterId } from '../domain/bibleCanon';
import {
  bookRepository,
  progressRepository,
  readingAggregateRepository,
  sessionRepository,
  settingsRepository
} from '../repositories/dexie/repositories';
import { createId, nextInstantAfter, nowInstant, progressValue, toLocalDate } from '../utils/core';

const cloneProgressWithValue = (progress: ReadingProgress, value: number): ReadingProgress => {
  if (progress.kind === 'pages') return { ...progress, currentPage: Math.round(value) };
  if (progress.kind === 'audio_time') return { ...progress, currentMinutes: Math.round(value) };
  if (progress.kind === 'bible_chapters') return progress;
  if (progress.kind === 'epub') return { ...progress, currentPercent: value };
  return { ...progress, currentPercent: value };
};

const entryCurrentValue = (entry: ReadingProgressEntry): number => {
  if (entry.kind === 'pages') return entry.currentPage;
  if (entry.kind === 'audio_time') return entry.currentMinutes;
  if (entry.kind === 'bible_chapters') return entry.endPage;
  return entry.currentPercent;
};

const entryPreviousValue = (entry: ReadingProgressEntry): number => {
  if (entry.kind === 'pages') return entry.previousPage;
  if (entry.kind === 'audio_time') return entry.previousMinutes;
  if (entry.kind === 'bible_chapters') return entry.startPage;
  return entry.previousPercent;
};

const buildProgressEntry = (
  book: Book,
  previous: number,
  current: number,
  source: ReadingProgressEntry['source'],
  options: {
    sessionId?: string;
    correctionOfEntryIds?: string[];
    occurredOn?: LocalDate;
    readingTime?: ReadingTimeContribution;
    recordedAt?: Instant;
  }
): ReadingProgressEntry => {
  const recordedAt = options.recordedAt ?? nowInstant();
  const common = {
    id: createId(), bookId: book.id, source, recordedAt, occurredOn: options.occurredOn ?? toLocalDate(), createdAt: recordedAt,
    ...(options.sessionId ? { sessionId: options.sessionId } : {}),
    ...(options.correctionOfEntryIds?.length ? { correctionOfEntryIds: options.correctionOfEntryIds } : {}),
    ...(options.readingTime ? { readingTime: options.readingTime } : {})
  };
  if (book.progress.kind === 'pages') {
    return { ...common, kind: 'pages', previousPage: Math.round(previous), currentPage: Math.round(current), deltaPages: Math.max(0, Math.round(current - previous)) };
  }
  if (book.progress.kind === 'audio_time') {
    return { ...common, kind: 'audio_time', previousMinutes: Math.round(previous), currentMinutes: Math.round(current), deltaMinutes: Math.max(0, Math.round(current - previous)) };
  }
  if (book.progress.kind === 'bible_chapters') throw new Error('Use o fluxo específico de leitura bíblica.');
  if (book.progress.kind === 'epub') throw new Error('O progresso EPUB deve ser registrado pelo Reader.');
  return { ...common, kind: 'audio_percent', previousPercent: previous, currentPercent: current, deltaPercent: Math.max(0, current - previous) };
};

const validateNext = (progress: ReadingProgress, next: number): void => {
  if (!Number.isFinite(next) || next < 0) throw new Error('Informe um progresso válido.');
  if (progress.kind === 'pages') {
    if (!Number.isInteger(next)) throw new Error('A página deve ser um número inteiro.');
    if (progress.totalPages !== undefined && next > progress.totalPages) throw new Error('A página ultrapassa o total do livro.');
  } else if (progress.kind === 'audio_time') {
    if (!Number.isInteger(next) || next > progress.totalMinutes) throw new Error('A posição ultrapassa a duração do audiobook.');
  } else if ((progress.kind === 'audio_percent' || progress.kind === 'epub') && next > 100) throw new Error('O percentual deve ficar entre 0 e 100.');
};

const reachedEnd = (progress: ReadingProgress): boolean => {
  if (progress.kind === 'pages') return progress.totalPages !== undefined && progress.currentPage === progress.totalPages;
  if (progress.kind === 'audio_time') return progress.currentMinutes === progress.totalMinutes;
  if (progress.kind === 'bible_chapters') return progress.completedChapterIds.length === BIBLE_TOTAL_CHAPTERS;
  if (progress.kind === 'epub') return false;
  return progress.currentPercent === 100;
};

const validateOccurredOn = (occurredOn: LocalDate): void => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(occurredOn)) throw new Error('Informe uma data de leitura válida.');
  const parsed = new Date(`${occurredOn}T12:00:00`);
  if (Number.isNaN(parsed.getTime()) || toLocalDate(parsed) !== occurredOn) throw new Error('Informe uma data de leitura válida.');
  if (occurredOn > toLocalDate()) throw new Error('A data da leitura não pode estar no futuro.');
};

export interface ManualReadingEstimate {
  source: 'estimated';
  durationMs: number;
  pagesPerHour: number;
  sampleCount: number;
}

export type ManualReadingTimeInput =
  | { source: 'manual'; durationMs: number; startedAt?: Instant; finishedAt?: Instant }
  | { source: 'estimated' };

const MIN_ESTIMATE_SAMPLES = 2;
const MIN_ESTIMATE_PAGES = 10;
const MIN_ESTIMATE_DURATION_MS = 20 * 60_000;

export interface UpdateCompletedSessionInput {
  sessionId: string;
  endProgress: number;
  durationMs: number;
  startedOn: LocalDate;
  expectedUpdatedAt: Instant;
}

export interface DeleteCompletedSessionInput {
  sessionId: string;
  expectedUpdatedAt: Instant;
}

export interface UpdateManualReadingInput {
  entryId: string;
  endProgress: number;
  occurredOn: LocalDate;
  expectedBookUpdatedAt: Instant;
  readingTime?: { source: 'manual'; durationMs: number; startedAt?: Instant; finishedAt?: Instant } | null;
}

export interface DeleteManualReadingInput {
  entryId: string;
  expectedBookUpdatedAt: Instant;
}

export interface BibleReadingInput {
  bookCode: BibleBookCode;
  startChapter: number;
  endChapter: number;
  startPage: number;
  endPage: number;
  occurredOn?: LocalDate;
  readingTime?: Extract<ReadingTimeContribution, { source: 'manual' }>;
  expectedUpdatedAt?: Instant;
  completeBook?: boolean;
}

export interface UpdateBibleReadingInput extends Omit<BibleReadingInput, 'readingTime'> {
  entryId: string;
  expectedBookUpdatedAt: Instant;
  readingTime?: Extract<ReadingTimeContribution, { source: 'manual' }> | null;
}

export interface DeleteBibleReadingInput {
  entryId: string;
  expectedBookUpdatedAt: Instant;
}

export interface BibleContinuationSuggestion {
  bookCode: BibleBookCode;
  chapter: number;
  startPage: number;
}

export interface EpubSessionAcquisition {
  session: ReadingSession;
  created: boolean;
}

export interface UpdateBibleCompletedSessionInput extends BibleReadingInput {
  sessionId: string;
  durationMs: number;
  startedOn: LocalDate;
  expectedUpdatedAt: Instant;
}

const entryDelta = (entry: ReadingProgressEntry): number => entry.kind === 'pages'
  ? entry.deltaPages
  : entry.kind === 'audio_time'
    ? entry.deltaMinutes
    : entry.kind === 'bible_chapters'
      ? entry.pagesRead
      : entry.deltaPercent;

const validateBiblePages = (book: Book, startPage: number, endPage: number): number => {
  if (!Number.isInteger(startPage) || !Number.isInteger(endPage) || startPage < 0 || endPage <= startPage) {
    throw new Error('A página final precisa ser maior que a página inicial.');
  }
  if (book.progress.kind !== 'bible_chapters') throw new Error('Este livro não usa acompanhamento bíblico.');
  if (book.progress.totalPages !== undefined && endPage > book.progress.totalPages) {
    throw new Error('A página final ultrapassa o total desta edição.');
  }
  return endPage - startPage;
};

type BibleBook = BibleTrackedBook;

const buildBibleEntry = (
  book: BibleBook,
  chapterIds: BibleChapterId[],
  startPage: number,
  endPage: number,
  source: ReadingProgressEntry['source'],
  options: {
    sessionId?: string;
    correctionOfEntryIds?: string[];
    occurredOn?: LocalDate;
    readingTime?: ReadingTimeContribution;
    recordedAt?: Instant;
  } = {}
): Extract<ReadingProgressEntry, { kind: 'bible_chapters' }> => {
  const recordedAt = options.recordedAt ?? nowInstant();
  return {
    id: createId(), bookId: book.id, kind: 'bible_chapters', source,
    chapterIds: normalizeBibleChapterIds(chapterIds),
    startPage, endPage, pagesRead: endPage - startPage,
    recordedAt, occurredOn: options.occurredOn ?? toLocalDate(), createdAt: recordedAt,
    ...(options.sessionId ? { sessionId: options.sessionId } : {}),
    ...(options.correctionOfEntryIds?.length ? { correctionOfEntryIds: options.correctionOfEntryIds } : {}),
    ...(options.readingTime ? { readingTime: options.readingTime } : {})
  };
};

const bibleCoverageFromEntries = (
  entries: ReadingProgressEntry[],
  excludeIds = new Set<string>(),
  additionalChapterIds: readonly BibleChapterId[] = []
): BibleChapterId[] => normalizeBibleChapterIds([
  ...entries.flatMap((entry) => entry.kind === 'bible_chapters' && !entry.supersededByEntryId && !excludeIds.has(entry.id)
    ? entry.chapterIds
    : []),
  ...additionalChapterIds
]);

const revisionInstant = (...values: Instant[]): Instant =>
  nextInstantAfter(values.reduce((latest, value) => value > latest ? value : latest));

const validateManualReadingTime = (readingTime: Extract<ReadingTimeContribution, { source: 'manual' }>): void => {
  if (!Number.isInteger(readingTime.durationMs) || readingTime.durationMs <= 0) {
    throw new Error('Informe um tempo de leitura válido.');
  }
  const hasStartedAt = readingTime.startedAt !== undefined;
  const hasFinishedAt = readingTime.finishedAt !== undefined;
  if (hasStartedAt !== hasFinishedAt) throw new Error('Informe os horários inicial e final completos.');
  if (!readingTime.startedAt || !readingTime.finishedAt) return;
  const startedMs = Date.parse(readingTime.startedAt);
  const finishedMs = Date.parse(readingTime.finishedAt);
  if (!Number.isFinite(startedMs) || !Number.isFinite(finishedMs) || finishedMs <= startedMs) {
    throw new Error('O horário final precisa ser posterior ao horário inicial.');
  }
  if (finishedMs - startedMs !== readingTime.durationMs) {
    throw new Error('A duração precisa corresponder ao intervalo informado.');
  }
};

const statusAfterHistoricalRevision = (
  original: Book,
  updated: Book,
  now: Instant,
  changedOn: LocalDate
): BookStatusHistory | undefined => {
  if (original.status !== 'finished' || reachedEnd(updated.progress)) return undefined;
  updated.status = 'reading';
  delete updated.completionOrigin;
  delete updated.historicalCompletion;
  delete updated.finishedAt;
  delete updated.finishedOn;
  return {
    id: createId(), bookId: original.id, fromStatus: 'finished', toStatus: 'reading',
    reason: 'progress_correction_reopen', changedAt: now, changedOn, createdAt: now
  };
};

const entryHasSourceInLineage = (
  entry: ReadingProgressEntry,
  source: ReadingProgressEntry['source'],
  entriesById: Map<string, ReadingProgressEntry>,
  visited = new Set<string>()
): boolean => {
  if (entry.source === source) return true;
  if (visited.has(entry.id)) return false;
  visited.add(entry.id);
  return entry.correctionOfEntryIds?.some((id) => {
    const corrected = entriesById.get(id);
    return corrected ? entryHasSourceInLineage(corrected, source, entriesById, visited) : false;
  }) ?? false;
};

const entryLogicalRecordedAt = (
  entry: ReadingProgressEntry,
  entriesById: Map<string, ReadingProgressEntry>,
  visited = new Set<string>()
): Instant => {
  if (entry.source !== 'correction' || visited.has(entry.id)) return entry.recordedAt;
  visited.add(entry.id);
  const ancestors = (entry.correctionOfEntryIds ?? [])
    .flatMap((id) => entriesById.get(id) ? [entriesById.get(id)!] : [])
    .map((ancestor) => entryLogicalRecordedAt(ancestor, entriesById, visited))
    .sort();
  return ancestors[0] ?? entry.recordedAt;
};

const estimateReadingTimeForDelta = (
  delta: number,
  entries: ReadingProgressEntry[],
  sessions: ReadingSession[],
  excludedEntryIds = new Set<string>()
): ManualReadingEstimate | undefined => {
  if (!Number.isInteger(delta) || delta <= 0) return undefined;
  const completedById = new Map(sessions
    .filter((session): session is CompletedReadingSession => session.status === 'completed')
    .map((session) => [session.id, session]));
  let sampleCount = 0;
  let samplePages = 0;
  let sampleDurationMs = 0;
  for (const entry of entries) {
    if (
      excludedEntryIds.has(entry.id)
      || entry.supersededByEntryId
      || entry.kind !== 'pages'
      || entry.deltaPages <= 0
      || entry.isBaseline
    ) continue;
    if (entry.sessionId) {
      const session = completedById.get(entry.sessionId);
      if (!session || session.durationMs <= 0) continue;
      sampleCount += 1;
      samplePages += entry.deltaPages;
      sampleDurationMs += session.durationMs;
    } else if (entry.readingTime?.source === 'manual') {
      sampleCount += 1;
      samplePages += entry.deltaPages;
      sampleDurationMs += entry.readingTime.durationMs;
    }
  }
  if (
    sampleCount < MIN_ESTIMATE_SAMPLES
    || samplePages < MIN_ESTIMATE_PAGES
    || sampleDurationMs < MIN_ESTIMATE_DURATION_MS
  ) return undefined;
  const pagesPerHour = samplePages / (sampleDurationMs / 3_600_000);
  const rawDurationMs = (delta / pagesPerHour) * 3_600_000;
  const durationMs = Math.max(60_000, Math.round(rawDurationMs / 60_000) * 60_000);
  return { source: 'estimated', durationMs, pagesPerHour, sampleCount };
};

const loadCompletedSessionRevision = async (sessionId: string, expectedUpdatedAt: Instant) => {
  const session = await sessionRepository.get(sessionId);
  if (!session || session.status !== 'completed') throw new Error('Sessão concluída não encontrada.');
  if (session.updatedAt !== expectedUpdatedAt) {
    throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
  }
  const book = await bookRepository.get(session.bookId);
  if (!book) throw new Error('O livro desta sessão não existe mais.');
  if (
    book.progress.kind !== session.startProgress.kind
    || book.progress.kind !== session.endProgress.kind
  ) throw new Error('O tipo de progresso desta sessão não é mais compatível com o livro.');
  if (await sessionRepository.findOpenByBook(book.id)) {
    throw new Error('Finalize a sessão atual antes de alterar uma sessão do histórico.');
  }

  const [entries, sessions] = await Promise.all([
    progressRepository.listByBook(book.id),
    sessionRepository.listByBook(book.id)
  ]);
  const linked = entries.filter((entry) => entry.sessionId === session.id && !entry.supersededByEntryId);
  if (linked.length !== 1) {
    throw new Error('Não foi possível identificar com segurança o progresso efetivo desta sessão.');
  }
  const effectiveEntry = linked[0];
  if (
    !effectiveEntry
    || effectiveEntry.kind !== book.progress.kind
    || entryPreviousValue(effectiveEntry) !== progressValue(session.startProgress)
    || entryCurrentValue(effectiveEntry) !== progressValue(session.endProgress)
  ) throw new Error('A cadeia de correções desta sessão não permite uma alteração segura.');

  const sessionsById = new Map(sessions.map((item) => [item.id, item]));
  const chronology = (entry: ReadingProgressEntry): [string, string] => [
    entry.sessionId ? sessionsById.get(entry.sessionId)?.startedAt ?? entry.recordedAt : entry.recordedAt,
    entry.recordedAt
  ];
  const targetChronology = session.startedAt;
  const laterEntries = entries
    .filter((entry) => !entry.supersededByEntryId && !entry.isBaseline && entry.id !== effectiveEntry.id && entry.kind === book.progress.kind)
    .filter((entry) => {
      const [primary, secondary] = chronology(entry);
      return primary > targetChronology || (primary === targetChronology && secondary > effectiveEntry.recordedAt);
    })
    .sort((a, b) => {
      const [aPrimary, aSecondary] = chronology(a);
      const [bPrimary, bSecondary] = chronology(b);
      return aPrimary.localeCompare(bPrimary) || aSecondary.localeCompare(bSecondary);
    });
  const isProgressAnchor = (entry: ReadingProgressEntry): boolean => {
    if (entryDelta(entry) > 0) return true;
    if (!entry.sessionId) return false;
    return sessionsById.get(entry.sessionId)?.status === 'completed';
  };
  return { session, book, effectiveEntry, laterEntries, isProgressAnchor };
};

const loadManualReadingRevision = async (entryId: string, expectedBookUpdatedAt: Instant) => {
  const entry = (await progressRepository.listAll()).find(({ id }) => id === entryId);
  if (!entry || entry.supersededByEntryId || entry.isBaseline || entry.sessionId) {
    throw new Error('Leitura registrada não encontrada.');
  }
  const book = await bookRepository.get(entry.bookId);
  if (!book) throw new Error('O livro desta leitura não existe mais.');
  if (book.updatedAt !== expectedBookUpdatedAt) {
    throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
  }
  if (book.progress.kind !== entry.kind) throw new Error('O tipo de progresso desta leitura não é mais compatível com o livro.');
  if (await sessionRepository.findOpenByBook(book.id)) {
    throw new Error('Finalize a sessão atual antes de alterar uma leitura do histórico.');
  }
  const [entries, sessions] = await Promise.all([
    progressRepository.listByBook(book.id),
    sessionRepository.listByBook(book.id)
  ]);
  const entriesById = new Map(entries.map((item) => [item.id, item]));
  if (!entryHasSourceInLineage(entry, 'manual', entriesById)) {
    throw new Error('Este registro não corresponde a uma leitura registrada.');
  }
  const sessionsById = new Map(sessions.map((item) => [item.id, item]));
  const chronology = (item: ReadingProgressEntry): [string, string] => [
    item.sessionId
      ? sessionsById.get(item.sessionId)?.startedAt ?? item.recordedAt
      : entryLogicalRecordedAt(item, entriesById),
    item.recordedAt
  ];
  const [targetPrimary, targetSecondary] = chronology(entry);
  const isTombstone = (item: ReadingProgressEntry): boolean =>
    item.source === 'correction' && !item.sessionId && entryDelta(item) === 0 && !item.readingTime;
  const isProgressAnchor = (item: ReadingProgressEntry): boolean => {
    if (isTombstone(item)) return false;
    if (entryDelta(item) > 0) return true;
    if (item.sessionId) return sessionsById.get(item.sessionId)?.status === 'completed';
    return entryHasSourceInLineage(item, 'manual', entriesById);
  };
  const laterEntries = entries
    .filter((item) => !item.supersededByEntryId && !item.isBaseline && item.id !== entry.id && item.kind === book.progress.kind)
    .filter((item) => {
      const [primary, secondary] = chronology(item);
      return primary > targetPrimary || (primary === targetPrimary && secondary > targetSecondary);
    })
    .sort((a, b) => {
      const [aPrimary, aSecondary] = chronology(a);
      const [bPrimary, bSecondary] = chronology(b);
      return aPrimary.localeCompare(bPrimary) || aSecondary.localeCompare(bSecondary);
    });
  return { entry, book, entries, sessions, laterEntries, isProgressAnchor };
};

export const readingService = {
  async recordProgress(
    bookId: string,
    next: number,
    options: {
      source?: ReadingProgressEntry['source'];
      sessionId?: string;
      completeBook?: boolean;
      confirmReopen?: boolean;
      completedSession?: CompletedReadingSession;
      clearActiveSession?: boolean;
      occurredOn?: LocalDate;
      readingTime?: ReadingTimeContribution;
      blockOpenSession?: boolean;
      expectedUpdatedAt?: Instant;
      expectedSession?: Pick<ReadingSession, 'id' | 'status' | 'updatedAt'>;
    } = {}
  ): Promise<Book> {
    const book = await bookRepository.get(bookId);
    if (!book) throw new Error('Livro não encontrado.');
    if (options.expectedUpdatedAt && book.updatedAt !== options.expectedUpdatedAt) {
      throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
    }
    if (book.completionOrigin === 'historical') throw new Error('Edite o registro histórico para corrigir seus dados.');
    if (options.completedSession && (
      book.status !== 'reading'
      || book.progress.kind !== options.completedSession.startProgress.kind
      || book.progress.kind !== options.completedSession.endProgress.kind
    )) {
      throw new Error('O livro mudou durante a sessão. A sessão foi preservada para correção.');
    }
    validateNext(book.progress, next);
    const current = progressValue(book.progress);
    if (next > current && (options.source ?? 'manual') === 'manual' && book.status !== 'reading') {
      throw new Error('Registrar leitura está disponível apenas para livros em andamento.');
    }
    if (!options.completedSession && await sessionRepository.findOpenByBook(bookId)) {
      throw new Error('Finalize a sessão atual antes de alterar o progresso do livro.');
    }
    const entries = (await progressRepository.listByBook(bookId))
      .filter((entry) => !entry.supersededByEntryId && entry.kind === book.progress.kind)
      .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
    const isCorrection = next < current;
    if (book.status === 'finished' && isCorrection && !options.confirmReopen) {
      throw new Error('REOPEN_CONFIRMATION_REQUIRED');
    }

    const affected: ReadingProgressEntry[] = [];
    let previous = current;
    if (isCorrection) {
      for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (!entry || entryCurrentValue(entry) <= next) break;
        affected.push(entry);
        previous = entryPreviousValue(entry);
        if (previous <= next) break;
      }
      if (previous > next) previous = 0;
    }
    const replacesEffectiveBaseline = isCorrection && affected.some(({ isBaseline }) => isBaseline);

    const now = nextInstantAfter(book.updatedAt);
    const activityDate = options.occurredOn ?? toLocalDate();
    const updated = { ...book, progress: cloneProgressWithValue(book.progress, next), updatedAt: now, lastActivityAt: now } as Book;
    let statusEvent: BookStatusHistory | undefined;

    if (book.status === 'finished' && isCorrection) {
      updated.status = 'reading';
      delete updated.completionOrigin;
      delete updated.historicalCompletion;
      delete updated.finishedAt;
      delete updated.finishedOn;
      statusEvent = {
        id: createId(), bookId, fromStatus: 'finished', toStatus: 'reading', reason: 'progress_correction_reopen',
        changedAt: now, changedOn: activityDate, createdAt: now
      };
    } else if (options.completeBook && reachedEnd(updated.progress) && book.status !== 'finished') {
      updated.status = 'finished';
      updated.completionOrigin = 'tracked';
      delete updated.historicalCompletion;
      updated.finishedAt = now;
      updated.finishedOn = activityDate;
      updated.startedAt ??= now;
      updated.startedOn ??= activityDate;
      statusEvent = {
        id: createId(), bookId, fromStatus: book.status, toStatus: 'finished', reason: 'progress_completion',
        changedAt: now, changedOn: activityDate, createdAt: now
      };
    }

    const correctedReadingTime = !replacesEffectiveBaseline
      ? options.readingTime ?? (isCorrection && affected.length === 1 ? affected[0]?.readingTime : undefined)
      : undefined;
    const residualEntry = isCorrection && !replacesEffectiveBaseline ? affected.at(-1) : undefined;
    const provenanceSessionId = options.sessionId ?? residualEntry?.sessionId;
    const entry = buildProgressEntry(
      updated,
      replacesEffectiveBaseline ? next : previous,
      next,
      replacesEffectiveBaseline ? 'baseline' : isCorrection ? 'correction' : (options.source ?? 'manual'),
      {
      ...(provenanceSessionId ? { sessionId: provenanceSessionId } : {}),
      ...(affected.length ? { correctionOfEntryIds: affected.map(({ id }) => id) } : {}),
      recordedAt: now,
      ...(options.occurredOn
        ? { occurredOn: options.occurredOn }
        : residualEntry
          ? { occurredOn: residualEntry.occurredOn }
          : {}),
      ...(correctedReadingTime ? { readingTime: correctedReadingTime } : {})
      }
    );
    if (replacesEffectiveBaseline) entry.isBaseline = true;
    let settings: AppSettings | undefined;
    if (options.clearActiveSession) {
      settings = await settingsRepository.ensureInitialized();
      delete settings.activeSessionId;
      settings.updatedAt = now;
    }
    await readingAggregateRepository.commitProgress(
      updated,
      entry,
      affected.map(({ id }) => id),
      statusEvent,
      options.completedSession,
      settings,
      options.expectedUpdatedAt ?? book.updatedAt,
      options.blockOpenSession ?? !options.completedSession,
      options.expectedSession
    );
    return updated;
  },

  async estimateManualReading(bookId: string, next: number, expectedUpdatedAt?: Instant): Promise<ManualReadingEstimate | undefined> {
    const book = await bookRepository.get(bookId);
    if (expectedUpdatedAt && book?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
    }
    if (!book || book.status !== 'reading' || book.progress.kind !== 'pages') return undefined;
    validateNext(book.progress, next);
    const delta = Math.round(next - book.progress.currentPage);
    if (delta <= 0) return undefined;
    const [entries, sessions] = await Promise.all([
      progressRepository.listByBook(bookId),
      sessionRepository.listByBook(bookId)
    ]);
    return estimateReadingTimeForDelta(delta, entries, sessions);
  },

  async recordManualReading(
    bookId: string,
    next: number,
    options: { occurredOn?: LocalDate; readingTime?: ManualReadingTimeInput; completeBook?: boolean; expectedUpdatedAt?: Instant } = {}
  ): Promise<Book> {
    const book = await bookRepository.get(bookId);
    if (!book) throw new Error('Livro não encontrado.');
    const expectedUpdatedAt = options.expectedUpdatedAt ?? book.updatedAt;
    if (book.updatedAt !== expectedUpdatedAt) {
      throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
    }
    if (book.status !== 'reading') throw new Error('Registrar leitura está disponível apenas para livros em andamento.');
    if (await sessionRepository.findOpenByBook(bookId)) {
      throw new Error('Finalize a sessão atual antes de registrar uma leitura manual.');
    }
    const occurredOn = options.occurredOn ?? toLocalDate();
    validateOccurredOn(occurredOn);
    validateNext(book.progress, next);
    if (next <= progressValue(book.progress)) throw new Error('O novo progresso precisa ser maior que o progresso atual.');
    let readingTime: ReadingTimeContribution | undefined;
    if (options.readingTime?.source === 'manual') {
      validateManualReadingTime(options.readingTime);
      readingTime = options.readingTime;
    } else if (options.readingTime?.source === 'estimated') {
      readingTime = await this.estimateManualReading(bookId, next, expectedUpdatedAt);
      if (!readingTime) throw new Error('Ainda não há histórico confiável para estimar o tempo desta leitura.');
    }
    return this.recordProgress(bookId, next, {
      source: 'manual',
      occurredOn,
      blockOpenSession: true,
      expectedUpdatedAt,
      ...(options.completeBook !== undefined ? { completeBook: options.completeBook } : {}),
      ...(readingTime ? { readingTime } : {})
    });
  },

  async recordBibleReading(bookId: string, input: BibleReadingInput): Promise<Book> {
    const book = await bookRepository.get(bookId);
    if (!book || book.progress.kind !== 'bible_chapters') throw new Error('Bíblia não encontrada.');
    if (input.expectedUpdatedAt && book.updatedAt !== input.expectedUpdatedAt) {
      throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
    }
    if (book.status !== 'reading') throw new Error('Registrar leitura está disponível apenas para livros em andamento.');
    if (await sessionRepository.findOpenByBook(bookId)) throw new Error('Finalize a sessão atual antes de registrar uma leitura manual.');
    const occurredOn = input.occurredOn ?? toLocalDate();
    validateOccurredOn(occurredOn);
    validateBiblePages(book, input.startPage, input.endPage);
    if (input.readingTime) validateManualReadingTime(input.readingTime);
    const chapterIds = bibleChapterRange(input.bookCode, input.startChapter, input.endChapter);
    const now = nextInstantAfter(book.updatedAt);
    const updated = {
      ...book,
      progress: {
        ...book.progress,
        completedChapterIds: normalizeBibleChapterIds([...book.progress.completedChapterIds, ...chapterIds])
      },
      updatedAt: now,
      lastActivityAt: now,
      startedAt: book.startedAt ?? now,
      startedOn: book.startedOn ?? occurredOn
    } as BibleBook;
    let statusEvent: BookStatusHistory | undefined;
    if (input.completeBook && updated.progress.completedChapterIds.length === BIBLE_TOTAL_CHAPTERS) {
      updated.status = 'finished';
      updated.completionOrigin = 'tracked';
      updated.finishedAt = now;
      updated.finishedOn = occurredOn;
      statusEvent = {
        id: createId(), bookId: book.id, fromStatus: book.status, toStatus: 'finished', reason: 'progress_completion',
        changedAt: now, changedOn: occurredOn, createdAt: now
      };
    }
    const entry = buildBibleEntry(updated, chapterIds, input.startPage, input.endPage, 'manual', {
      occurredOn, recordedAt: now, ...(input.readingTime ? { readingTime: input.readingTime } : {})
    });
    await readingAggregateRepository.commitProgress(updated, entry, [], statusEvent, undefined, undefined, book.updatedAt, true);
    return updated;
  },

  async updateBibleReading(input: UpdateBibleReadingInput): Promise<Book> {
    validateOccurredOn(input.occurredOn ?? toLocalDate());
    const entry = (await progressRepository.listAll()).find(({ id }) => id === input.entryId);
    if (!entry || entry.kind !== 'bible_chapters' || entry.supersededByEntryId || entry.isBaseline || entry.sessionId) {
      throw new Error('Leitura bíblica registrada não encontrada.');
    }
    const book = await bookRepository.get(entry.bookId);
    if (!book || book.progress.kind !== 'bible_chapters') throw new Error('A Bíblia desta leitura não existe mais.');
    if (book.updatedAt !== input.expectedBookUpdatedAt) throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
    if (await sessionRepository.findOpenByBook(book.id)) throw new Error('Finalize a sessão atual antes de alterar uma leitura do histórico.');
    validateBiblePages(book, input.startPage, input.endPage);
    if (input.readingTime) validateManualReadingTime(input.readingTime);
    const chapterIds = bibleChapterRange(input.bookCode, input.startChapter, input.endChapter);
    const entries = await progressRepository.listByBook(book.id);
    const now = revisionInstant(book.updatedAt, entry.recordedAt);
    const updated = {
      ...book,
      progress: { ...book.progress, completedChapterIds: bibleCoverageFromEntries(entries, new Set([entry.id]), chapterIds) },
      updatedAt: now,
      lastActivityAt: now
    } as BibleBook;
    const correction = buildBibleEntry(updated, chapterIds, input.startPage, input.endPage, 'correction', {
      correctionOfEntryIds: [entry.id], occurredOn: input.occurredOn ?? entry.occurredOn, recordedAt: now,
      ...(input.readingTime === null ? {} : input.readingTime ? { readingTime: input.readingTime } : entry.readingTime ? { readingTime: entry.readingTime } : {})
    });
    const statusEvent = statusAfterHistoricalRevision(book, updated, now, input.occurredOn ?? entry.occurredOn);
    await readingAggregateRepository.reviseProgressEntry(updated, correction, { id: entry.id, recordedAt: entry.recordedAt }, book.updatedAt, statusEvent);
    return updated;
  },

  async deleteBibleReading(input: DeleteBibleReadingInput): Promise<Book> {
    const entry = (await progressRepository.listAll()).find(({ id }) => id === input.entryId);
    if (!entry || entry.kind !== 'bible_chapters' || entry.supersededByEntryId || entry.isBaseline || entry.sessionId) {
      throw new Error('Leitura bíblica registrada não encontrada.');
    }
    const book = await bookRepository.get(entry.bookId);
    if (!book || book.progress.kind !== 'bible_chapters') throw new Error('A Bíblia desta leitura não existe mais.');
    if (book.updatedAt !== input.expectedBookUpdatedAt) throw new Error('Este livro foi alterado em outra tela. Recarregue os dados antes de continuar.');
    const entries = await progressRepository.listByBook(book.id);
    const now = revisionInstant(book.updatedAt, entry.recordedAt);
    const updated = {
      ...book,
      progress: { ...book.progress, completedChapterIds: bibleCoverageFromEntries(entries, new Set([entry.id])) },
      updatedAt: now,
      lastActivityAt: now
    } as BibleBook;
    const tombstone = buildBibleEntry(updated, [], 0, 0, 'correction', {
      correctionOfEntryIds: [entry.id], occurredOn: entry.occurredOn, recordedAt: now
    });
    const statusEvent = statusAfterHistoricalRevision(book, updated, now, entry.occurredOn);
    await readingAggregateRepository.reviseProgressEntry(updated, tombstone, { id: entry.id, recordedAt: entry.recordedAt }, book.updatedAt, statusEvent);
    return updated;
  },

  async updateManualReading(input: UpdateManualReadingInput): Promise<Book> {
    validateOccurredOn(input.occurredOn);
    const { entry, book, entries, sessions, laterEntries, isProgressAnchor } = await loadManualReadingRevision(
      input.entryId,
      input.expectedBookUpdatedAt
    );
    validateNext(book.progress, input.endProgress);
    const start = entryPreviousValue(entry);
    if (input.endProgress <= start) throw new Error('O progresso final precisa ser maior que o progresso inicial da leitura.');
    const nextEntry = laterEntries.find(isProgressAnchor);
    if (nextEntry && input.endProgress > entryPreviousValue(nextEntry)) {
      throw new Error(`O progresso final não pode ultrapassar o início do próximo registro (${entryPreviousValue(nextEntry)}).`);
    }

    let readingTime = entry.readingTime;
    if (input.readingTime !== undefined) {
      if (input.readingTime === null) readingTime = undefined;
      else {
        validateManualReadingTime(input.readingTime);
        readingTime = input.readingTime;
      }
    } else if (entry.readingTime?.source === 'estimated' && input.endProgress !== entryCurrentValue(entry)) {
      if (book.progress.kind !== 'pages') throw new Error('Não é possível recalcular a estimativa para este tipo de progresso.');
      readingTime = estimateReadingTimeForDelta(
        Math.round(input.endProgress - start),
        entries,
        sessions,
        new Set([entry.id])
      );
      if (!readingTime) {
        throw new Error('Não há histórico confiável suficiente para recalcular o tempo estimado desta leitura.');
      }
    }

    const now = revisionInstant(book.updatedAt, entry.recordedAt);
    const isLatestProgress = !nextEntry;
    const followsCurrent = progressValue(book.progress) === entryCurrentValue(entry);
    const updatedBook = {
      ...book,
      progress: isLatestProgress && followsCurrent ? cloneProgressWithValue(book.progress, input.endProgress) : book.progress,
      updatedAt: now,
      lastActivityAt: now
    } as Book;
    const statusEvent = statusAfterHistoricalRevision(book, updatedBook, now, input.occurredOn);
    const correction = buildProgressEntry(updatedBook, start, input.endProgress, 'correction', {
      correctionOfEntryIds: [entry.id],
      occurredOn: input.occurredOn,
      recordedAt: now,
      ...(readingTime ? { readingTime } : {})
    });
    await readingAggregateRepository.reviseProgressEntry(
      updatedBook,
      correction,
      { id: entry.id, recordedAt: entry.recordedAt },
      book.updatedAt,
      statusEvent
    );
    return updatedBook;
  },

  async deleteManualReading(input: DeleteManualReadingInput): Promise<Book> {
    const { entry, book, laterEntries, isProgressAnchor } = await loadManualReadingRevision(
      input.entryId,
      input.expectedBookUpdatedAt
    );
    const start = entryPreviousValue(entry);
    const nextEntry = laterEntries.find(isProgressAnchor);
    const now = revisionInstant(book.updatedAt, entry.recordedAt);
    const isLatestProgress = !nextEntry;
    const followsCurrent = progressValue(book.progress) === entryCurrentValue(entry);
    const updatedBook = {
      ...book,
      progress: isLatestProgress && followsCurrent ? cloneProgressWithValue(book.progress, start) : book.progress,
      updatedAt: now,
      lastActivityAt: now
    } as Book;
    const statusEvent = statusAfterHistoricalRevision(book, updatedBook, now, entry.occurredOn);
    const tombstone = buildProgressEntry(updatedBook, start, start, 'correction', {
      correctionOfEntryIds: [entry.id],
      occurredOn: entry.occurredOn,
      recordedAt: now
    });
    await readingAggregateRepository.reviseProgressEntry(
      updatedBook,
      tombstone,
      { id: entry.id, recordedAt: entry.recordedAt },
      book.updatedAt,
      statusEvent
    );
    return updatedBook;
  },

  async startSession(bookId: string): Promise<ReadingSession> {
    const book = await bookRepository.get(bookId);
    if (!book || book.status !== 'reading') throw new Error('Marque o livro como “Lendo” antes de iniciar.');
    const now = nowInstant();
    const session: ReadingSession = {
      id: createId(), bookId, status: 'active', startedAt: now, startedOn: toLocalDate(), accumulatedPausedMs: 0,
      startProgress: structuredClone(book.progress), createdAt: now, updatedAt: now
    };
    const settings = await settingsRepository.ensureInitialized();
    settings.activeSessionId = session.id;
    settings.updatedAt = now;
    await readingAggregateRepository.startSession(session, settings, book.updatedAt);
    return session;
  },

  async startOrReuseEpubSession(bookId: string): Promise<EpubSessionAcquisition> {
    const book = await bookRepository.get(bookId);
    if (!book || book.status !== 'reading' || book.progress.kind !== 'epub') {
      throw new Error('O EPUB precisa estar marcado como “Lendo” antes de iniciar.');
    }
    const documentSha256 = book.progress.documentSha256;
    const compatible = (session: ReadingSession): boolean => session.bookId === book.id
      && session.startProgress.kind === 'epub'
      && session.startProgress.documentSha256 === documentSha256;
    const existing = await sessionRepository.findOpen();
    if (existing) {
      if (!compatible(existing)) throw new Error('Já existe outra sessão de leitura em andamento.');
      return { session: existing, created: false };
    }
    try {
      return { session: await this.startSession(bookId), created: true };
    } catch (error) {
      const concurrent = await sessionRepository.findOpen();
      if (concurrent && compatible(concurrent)) return { session: concurrent, created: false };
      throw error;
    }
  },

  async pauseSession(id: string, expectedUpdatedAt?: Instant): Promise<ReadingSession> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status !== 'active') throw new Error('Sessão ativa não encontrada.');
    const now = nextInstantAfter(session.updatedAt);
    const paused: ReadingSession = { ...session, status: 'paused', pausedAt: now, updatedAt: now };
    await readingAggregateRepository.updateSession(paused, {
      id: session.id, status: 'active', updatedAt: expectedUpdatedAt ?? session.updatedAt
    });
    return paused;
  },

  async pauseSessionAt(id: string, pausedAt: Instant, expectedUpdatedAt?: Instant): Promise<ReadingSession> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status !== 'active') throw new Error('Sessão ativa não encontrada.');
    const pausedMs = Date.parse(pausedAt);
    const updatedMs = Date.parse(session.updatedAt);
    if (!Number.isFinite(pausedMs) || pausedMs < updatedMs || pausedMs > Date.now()) {
      throw new Error('O instante efetivo da pausa é inválido.');
    }
    const paused: ReadingSession = { ...session, status: 'paused', pausedAt, updatedAt: pausedAt };
    await readingAggregateRepository.updateSession(paused, {
      id: session.id, status: 'active', updatedAt: expectedUpdatedAt ?? session.updatedAt
    });
    return paused;
  },

  async resumeSession(id: string, expectedUpdatedAt?: Instant): Promise<ReadingSession> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status !== 'paused') throw new Error('Sessão pausada não encontrada.');
    const now = nextInstantAfter(session.updatedAt);
    const pauseDuration = Math.max(0, Date.parse(now) - Date.parse(session.pausedAt));
    const resumed: ReadingSession = {
      id: session.id,
      bookId: session.bookId,
      status: 'active',
      startedAt: session.startedAt,
      startedOn: session.startedOn,
      accumulatedPausedMs: session.accumulatedPausedMs + pauseDuration,
      startProgress: session.startProgress,
      createdAt: session.createdAt,
      updatedAt: now
    };
    await readingAggregateRepository.updateSession(resumed, {
      id: session.id, status: 'paused', updatedAt: expectedUpdatedAt ?? session.updatedAt
    });
    return resumed;
  },

  async resumeSessionAt(id: string, resumedAt: Instant, expectedUpdatedAt?: Instant): Promise<ReadingSession> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status !== 'paused') throw new Error('Sessão pausada não encontrada.');
    const resumedMs = Date.parse(resumedAt);
    const updatedMs = Date.parse(session.updatedAt);
    if (!Number.isFinite(resumedMs) || resumedMs < updatedMs || resumedMs > Date.now()) {
      throw new Error('O instante efetivo da retomada é inválido.');
    }
    const pauseDuration = Math.max(0, resumedMs - Date.parse(session.pausedAt));
    const resumed: ReadingSession = {
      id: session.id,
      bookId: session.bookId,
      status: 'active',
      startedAt: session.startedAt,
      startedOn: session.startedOn,
      accumulatedPausedMs: session.accumulatedPausedMs + pauseDuration,
      startProgress: session.startProgress,
      createdAt: session.createdAt,
      updatedAt: resumedAt
    };
    await readingAggregateRepository.updateSession(resumed, {
      id: session.id, status: 'paused', updatedAt: expectedUpdatedAt ?? session.updatedAt
    });
    return resumed;
  },

  async discardSession(id: string, expectedUpdatedAt?: Instant): Promise<void> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session) throw new Error('Sessão em andamento não encontrada.');
    if (session.status === 'completed') throw new Error('Uma sessão concluída não pode ser descartada.');
    await readingAggregateRepository.discardSession({
      id: session.id,
      status: session.status,
      updatedAt: expectedUpdatedAt ?? session.updatedAt
    });
  },

  async finishSession(id: string, next: number, completeBook = false, expectedUpdatedAt?: Instant): Promise<Book> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status === 'completed') throw new Error('Sessão em andamento não encontrada.');
    const now = nextInstantAfter(session.updatedAt);
    let pausedMs = session.accumulatedPausedMs;
    if (session.status === 'paused') pausedMs += Math.max(0, Date.parse(now) - Date.parse(session.pausedAt));
    const durationMs = Math.max(0, Date.parse(now) - Date.parse(session.startedAt) - pausedMs);
    const book = await bookRepository.get(session.bookId);
    if (!book) throw new Error('Livro da sessão não encontrado.');
    if (book.status !== 'reading' || book.progress.kind !== session.startProgress.kind) {
      throw new Error('O livro mudou durante a sessão. Recarregue os dados ou reverta a alteração antes de encerrar.');
    }
    validateNext(book.progress, next);
    if (next < progressValue(session.startProgress) || next < progressValue(book.progress)) {
      throw new Error('O progresso final da sessão não pode ser menor que o progresso inicial. Encerre sem avanço e faça uma correção separadamente.');
    }
    const completed: CompletedReadingSession = {
      id: session.id, bookId: session.bookId, status: 'completed', startedAt: session.startedAt, startedOn: session.startedOn,
      accumulatedPausedMs: pausedMs, startProgress: session.startProgress, endProgress: cloneProgressWithValue(book.progress, next),
      finishedAt: now, durationMs, createdAt: session.createdAt, updatedAt: now
    };
    // Regra V0: tempo e progresso da sessão inteira pertencem ao dia civil em que ela começou.
    return this.recordProgress(book.id, next, {
      source: 'session', sessionId: id, completeBook, completedSession: completed, clearActiveSession: true,
      occurredOn: session.startedOn,
      expectedSession: { id: session.id, status: session.status, updatedAt: expectedUpdatedAt ?? session.updatedAt }
    });
  },

  async finishEpubSession(id: string, expectedUpdatedAt?: Instant): Promise<Book> {
    const session = await sessionRepository.get(id);
    if (expectedUpdatedAt && session?.updatedAt !== expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status === 'completed') throw new Error('Sessão em andamento não encontrada.');
    const book = await bookRepository.get(session.bookId);
    if (!book || book.status !== 'reading' || book.progress.kind !== 'epub'
      || session.startProgress.kind !== 'epub'
      || session.startProgress.documentSha256 !== book.progress.documentSha256) {
      throw new Error('O EPUB mudou durante a sessão. A sessão foi preservada para recuperação.');
    }
    const anchor = session.updatedAt > book.updatedAt ? session.updatedAt : book.updatedAt;
    const now = nextInstantAfter(anchor);
    let pausedMs = session.accumulatedPausedMs;
    if (session.status === 'paused') pausedMs += Math.max(0, Date.parse(now) - Date.parse(session.pausedAt));
    const durationMs = Math.max(0, Date.parse(now) - Date.parse(session.startedAt) - pausedMs);
    const updated: Book = { ...book, lastActivityAt: now, updatedAt: now };
    const completed: CompletedReadingSession = {
      id: session.id,
      bookId: session.bookId,
      status: 'completed',
      startedAt: session.startedAt,
      startedOn: session.startedOn,
      accumulatedPausedMs: pausedMs,
      startProgress: session.startProgress,
      endProgress: structuredClone(book.progress),
      finishedAt: now,
      durationMs,
      createdAt: session.createdAt,
      updatedAt: now
    };
    const entry: EpubProgressEntry = {
      id: createId(),
      bookId: book.id,
      source: 'session',
      sessionId: session.id,
      kind: 'epub',
      documentSha256: book.progress.documentSha256,
      ...(session.startProgress.currentCfi ? { previousCfi: session.startProgress.currentCfi } : {}),
      ...(book.progress.currentCfi ? { currentCfi: book.progress.currentCfi } : {}),
      previousPercent: session.startProgress.currentPercent,
      currentPercent: book.progress.currentPercent,
      deltaPercent: Math.max(0, book.progress.currentPercent - session.startProgress.currentPercent),
      recordedAt: now,
      occurredOn: session.startedOn,
      createdAt: now
    };
    const settings = await settingsRepository.ensureInitialized();
    delete settings.activeSessionId;
    settings.updatedAt = now;
    await readingAggregateRepository.commitProgress(
      updated,
      entry,
      [],
      undefined,
      completed,
      settings,
      book.updatedAt,
      false,
      { id: session.id, status: session.status, updatedAt: expectedUpdatedAt ?? session.updatedAt }
    );
    return updated;
  },

  async finishBibleSession(
    id: string,
    input: BibleReadingInput & { completeBook?: boolean },
    expectedSessionUpdatedAt?: Instant
  ): Promise<Book> {
    const session = await sessionRepository.get(id);
    if (expectedSessionUpdatedAt && session?.updatedAt !== expectedSessionUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    if (!session || session.status === 'completed') throw new Error('Sessão em andamento não encontrada.');
    const book = await bookRepository.get(session.bookId);
    if (!book || book.progress.kind !== 'bible_chapters' || session.startProgress.kind !== 'bible_chapters') {
      throw new Error('A Bíblia mudou durante a sessão. A sessão foi preservada para correção.');
    }
    if (book.status !== 'reading') throw new Error('A Bíblia não está marcada como em leitura.');
    validateBiblePages(book, input.startPage, input.endPage);
    const chapterIds = bibleChapterRange(input.bookCode, input.startChapter, input.endChapter);
    const now = nextInstantAfter(session.updatedAt > book.updatedAt ? session.updatedAt : book.updatedAt);
    let pausedMs = session.accumulatedPausedMs;
    if (session.status === 'paused') pausedMs += Math.max(0, Date.parse(now) - Date.parse(session.pausedAt));
    const durationMs = Math.max(0, Date.parse(now) - Date.parse(session.startedAt) - pausedMs);
    const completedChapterIds = normalizeBibleChapterIds([...book.progress.completedChapterIds, ...chapterIds]);
    const updated = {
      ...book,
      progress: { ...book.progress, completedChapterIds },
      updatedAt: now,
      lastActivityAt: now,
      startedAt: book.startedAt ?? session.startedAt,
      startedOn: book.startedOn ?? session.startedOn
    } as BibleBook;
    let statusEvent: BookStatusHistory | undefined;
    if (input.completeBook && completedChapterIds.length === BIBLE_TOTAL_CHAPTERS) {
      updated.status = 'finished';
      updated.completionOrigin = 'tracked';
      updated.finishedAt = now;
      updated.finishedOn = session.startedOn;
      statusEvent = {
        id: createId(), bookId: book.id, fromStatus: book.status, toStatus: 'finished', reason: 'progress_completion',
        changedAt: now, changedOn: session.startedOn, createdAt: now
      };
    }
    const completed: CompletedReadingSession = {
      id: session.id, bookId: session.bookId, status: 'completed', startedAt: session.startedAt, startedOn: session.startedOn,
      accumulatedPausedMs: pausedMs, startProgress: session.startProgress, endProgress: structuredClone(updated.progress),
      finishedAt: now, durationMs, createdAt: session.createdAt, updatedAt: now
    };
    const entry = buildBibleEntry(updated, chapterIds, input.startPage, input.endPage, 'session', {
      sessionId: session.id, occurredOn: session.startedOn, recordedAt: now
    });
    const settings = await settingsRepository.ensureInitialized();
    delete settings.activeSessionId;
    settings.updatedAt = now;
    await readingAggregateRepository.commitProgress(
      updated, entry, [], statusEvent, completed, settings, book.updatedAt, false,
      { id: session.id, status: session.status, updatedAt: expectedSessionUpdatedAt ?? session.updatedAt }
    );
    return updated;
  },

  async bibleContinuation(bookId: string): Promise<BibleContinuationSuggestion | undefined> {
    const allEntries = await progressRepository.listByBook(bookId);
    const entriesById = new Map(allEntries.map((entry) => [entry.id, entry]));
    const entries = allEntries
      .filter((entry): entry is Extract<ReadingProgressEntry, { kind: 'bible_chapters' }> =>
        entry.kind === 'bible_chapters' && !entry.supersededByEntryId && !entry.isBaseline && entry.chapterIds.length > 0)
      .sort((left, right) => right.occurredOn.localeCompare(left.occurredOn)
        || entryLogicalRecordedAt(right, entriesById).localeCompare(entryLogicalRecordedAt(left, entriesById)));
    const latest = entries[0];
    if (!latest) return undefined;
    const lastId = latest.chapterIds.at(-1)!;
    const [bookCode, chapterText] = lastId.split(':') as [BibleBookCode, string];
    const currentChapter = Number(chapterText);
    const currentId = bibleChapterRange(bookCode, currentChapter, currentChapter)[0]!;
    const nextId = nextBibleChapter(currentId);
    const parsed = nextId ? parseBibleChapterId(nextId) : undefined;
    return parsed ? { bookCode: parsed.code, chapter: parsed.chapter, startPage: latest.endPage } : undefined;
  },

  async updateBibleCompletedSession(input: UpdateBibleCompletedSessionInput): Promise<Book> {
    validateOccurredOn(input.startedOn);
    if (!Number.isInteger(input.durationMs) || input.durationMs < 0) throw new Error('Informe uma duração válida para a sessão.');
    const session = await sessionRepository.get(input.sessionId);
    if (!session || session.status !== 'completed' || session.updatedAt !== input.expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    const book = await bookRepository.get(session.bookId);
    if (!book || book.progress.kind !== 'bible_chapters') throw new Error('A Bíblia desta sessão não existe mais.');
    if (await sessionRepository.findOpenByBook(book.id)) throw new Error('Finalize a sessão atual antes de alterar uma sessão do histórico.');
    validateBiblePages(book, input.startPage, input.endPage);
    const entries = await progressRepository.listByBook(book.id);
    const effectiveEntry = entries.find((entry) => entry.kind === 'bible_chapters' && entry.sessionId === session.id && !entry.supersededByEntryId);
    if (!effectiveEntry || effectiveEntry.kind !== 'bible_chapters') throw new Error('O progresso efetivo desta sessão não foi encontrado.');
    const chapterIds = bibleChapterRange(input.bookCode, input.startChapter, input.endChapter);
    const now = revisionInstant(session.updatedAt, book.updatedAt, effectiveEntry.recordedAt);
    const updated = {
      ...book,
      progress: { ...book.progress, completedChapterIds: bibleCoverageFromEntries(entries, new Set([effectiveEntry.id]), chapterIds) },
      updatedAt: now,
      lastActivityAt: now
    } as BibleBook;
    const correction = buildBibleEntry(updated, chapterIds, input.startPage, input.endPage, 'correction', {
      sessionId: session.id, correctionOfEntryIds: [effectiveEntry.id], occurredOn: input.startedOn, recordedAt: now
    });
    const updatedSession: CompletedReadingSession = {
      ...session, startedOn: input.startedOn, durationMs: input.durationMs,
      endProgress: structuredClone(updated.progress), updatedAt: now
    };
    const statusEvent = statusAfterHistoricalRevision(book, updated, now, input.startedOn);
    await readingAggregateRepository.reviseCompletedSession(
      updated, correction, effectiveEntry.id, book.updatedAt,
      { id: session.id, status: 'completed', updatedAt: input.expectedUpdatedAt }, updatedSession, statusEvent
    );
    return updated;
  },

  async deleteBibleCompletedSession(input: DeleteCompletedSessionInput): Promise<Book> {
    const session = await sessionRepository.get(input.sessionId);
    if (!session || session.status !== 'completed' || session.updatedAt !== input.expectedUpdatedAt) {
      throw new Error('Esta sessão foi alterada em outra tela. Recarregue antes de continuar.');
    }
    const book = await bookRepository.get(session.bookId);
    if (!book || book.progress.kind !== 'bible_chapters') throw new Error('A Bíblia desta sessão não existe mais.');
    const entries = await progressRepository.listByBook(book.id);
    const effectiveEntry = entries.find((entry) => entry.kind === 'bible_chapters' && entry.sessionId === session.id && !entry.supersededByEntryId);
    if (!effectiveEntry || effectiveEntry.kind !== 'bible_chapters') throw new Error('O progresso efetivo desta sessão não foi encontrado.');
    const now = revisionInstant(session.updatedAt, book.updatedAt, effectiveEntry.recordedAt);
    const updated = {
      ...book,
      progress: { ...book.progress, completedChapterIds: bibleCoverageFromEntries(entries, new Set([effectiveEntry.id])) },
      updatedAt: now,
      lastActivityAt: now
    } as BibleBook;
    const tombstone = buildBibleEntry(updated, [], 0, 0, 'correction', {
      correctionOfEntryIds: [effectiveEntry.id], occurredOn: session.startedOn, recordedAt: now
    });
    const statusEvent = statusAfterHistoricalRevision(book, updated, now, session.startedOn);
    await readingAggregateRepository.reviseCompletedSession(
      updated, tombstone, effectiveEntry.id, book.updatedAt,
      { id: session.id, status: 'completed', updatedAt: input.expectedUpdatedAt }, undefined, statusEvent
    );
    return updated;
  },

  async updateCompletedSession(input: UpdateCompletedSessionInput): Promise<Book> {
    validateOccurredOn(input.startedOn);
    if (!Number.isInteger(input.durationMs) || input.durationMs < 0) {
      throw new Error('Informe uma duração válida para a sessão.');
    }
    const { session, book, effectiveEntry, laterEntries, isProgressAnchor } = await loadCompletedSessionRevision(input.sessionId, input.expectedUpdatedAt);
    validateNext(book.progress, input.endProgress);
    const start = progressValue(session.startProgress);
    if (input.endProgress < start) throw new Error('O progresso final não pode ser menor que o progresso inicial da sessão.');
    const nextEntry = laterEntries.find(isProgressAnchor);
    if (nextEntry && input.endProgress > entryPreviousValue(nextEntry)) {
      throw new Error(`O progresso final não pode ultrapassar o início do próximo registro (${entryPreviousValue(nextEntry)}).`);
    }

    const now = revisionInstant(session.updatedAt, book.updatedAt, effectiveEntry.recordedAt);
    const isLatestProgress = !nextEntry;
    const followsCurrent = progressValue(book.progress) === entryCurrentValue(effectiveEntry);
    const updatedBook = {
      ...book,
      progress: isLatestProgress && followsCurrent ? cloneProgressWithValue(book.progress, input.endProgress) : book.progress,
      updatedAt: now,
      lastActivityAt: now
    } as Book;
    const statusEvent = statusAfterHistoricalRevision(book, updatedBook, now, input.startedOn);
    const correction = buildProgressEntry(updatedBook, start, input.endProgress, 'correction', {
      sessionId: session.id,
      correctionOfEntryIds: [effectiveEntry.id],
      occurredOn: input.startedOn,
      recordedAt: now
    });
    const updatedSession: CompletedReadingSession = {
      ...session,
      startedOn: input.startedOn,
      durationMs: input.durationMs,
      endProgress: cloneProgressWithValue(session.endProgress, input.endProgress),
      updatedAt: now
    };
    await readingAggregateRepository.reviseCompletedSession(
      updatedBook,
      correction,
      effectiveEntry.id,
      book.updatedAt,
      { id: session.id, status: 'completed', updatedAt: input.expectedUpdatedAt },
      updatedSession,
      statusEvent
    );
    return updatedBook;
  },

  async deleteCompletedSession(input: DeleteCompletedSessionInput): Promise<Book> {
    const { session, book, effectiveEntry, laterEntries, isProgressAnchor } = await loadCompletedSessionRevision(input.sessionId, input.expectedUpdatedAt);
    const start = progressValue(session.startProgress);
    const nextEntry = laterEntries.find(isProgressAnchor);
    const now = revisionInstant(session.updatedAt, book.updatedAt, effectiveEntry.recordedAt);
    const isLatestProgress = !nextEntry;
    const followsCurrent = progressValue(book.progress) === entryCurrentValue(effectiveEntry);
    const updatedBook = {
      ...book,
      progress: isLatestProgress && followsCurrent ? cloneProgressWithValue(book.progress, start) : book.progress,
      updatedAt: now,
      lastActivityAt: now
    } as Book;
    const statusEvent = statusAfterHistoricalRevision(book, updatedBook, now, session.startedOn);
    const correction = buildProgressEntry(updatedBook, start, start, 'correction', {
      correctionOfEntryIds: [effectiveEntry.id],
      occurredOn: session.startedOn,
      recordedAt: now
    });
    await readingAggregateRepository.reviseCompletedSession(
      updatedBook,
      correction,
      effectiveEntry.id,
      book.updatedAt,
      { id: session.id, status: 'completed', updatedAt: input.expectedUpdatedAt },
      undefined,
      statusEvent
    );
    return updatedBook;
  },

  elapsedMs(session: ReadingSession, at = Date.now()): number {
    if (session.status === 'completed') return session.durationMs;
    const end = session.status === 'paused' ? Date.parse(session.pausedAt) : at;
    return Math.max(0, end - Date.parse(session.startedAt) - session.accumulatedPausedMs);
  }
};
